import { useState } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import api from "../../lib/api";
import { getCategoryMeta } from "./categories";

const EXAMPLE_PROMPTS = [
  "Proveedores de pescado fresco",
  "Distribuidores de carnes premium",
  "Mayoristas de frutas y verduras",
  "Especialistas en producto japonés",
];

export default function SupplierAIDialog({ open, onOpenChange, restaurantId, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [provider, setProvider] = useState("");
  const [saving, setSaving] = useState(false);

  const resetAll = () => {
    setPrompt("");
    setSuggestions([]);
    setSelected(new Set());
    setProvider("");
  };

  const handleClose = (v) => {
    if (!v) resetAll();
    onOpenChange(v);
  };

  const handleSuggest = async () => {
    setLoading(true);
    setSuggestions([]);
    setSelected(new Set());
    try {
      const res = await api.suggestSuppliersWithPrompt(restaurantId, {
        prompt: prompt.trim(),
        count: 5,
      });
      const items = res?.suggestions || [];
      if (items.length === 0) {
        toast.error("La IA no devolvió sugerencias. Prueba reformulando.");
      } else {
        setSuggestions(items);
        setSelected(new Set(items.map((_, i) => i)));  // preselect all
        setProvider(res?._provider || "");
      }
    } catch (err) {
      toast.error(err.message || "Error consultando a la IA");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleCreate = async () => {
    const picks = suggestions.filter((_, i) => selected.has(i));
    if (picks.length === 0) {
      toast.error("Selecciona al menos un proveedor");
      return;
    }
    setSaving(true);
    try {
      const res = await api.bulkCreateSuppliers(restaurantId, picks);
      toast.success(`${res.created || picks.length} proveedor(es) añadido(s)`);
      onCreated?.(res.suppliers || []);
      handleClose(false);
    } catch (err) {
      toast.error(err.message || "Error al crear proveedores");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Sugerir proveedores con IA
          </DialogTitle>
          <DialogDescription>
            Describe qué proveedores necesitas. La IA tendrá en cuenta tu tipo de cocina y ubicación.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>¿Qué buscas? <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input
              placeholder="Ej: proveedores de pescado fresco en Barcelona centro"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) { e.preventDefault(); handleSuggest(); } }}
              data-testid="supplier-ai-prompt"
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {EXAMPLE_PROMPTS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPrompt(p)}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:border-violet-300 hover:bg-violet-50 text-muted-foreground transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {suggestions.length === 0 && (
            <Button
              onClick={handleSuggest}
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
              data-testid="supplier-ai-suggest-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Consultando a la IA…</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Obtener sugerencias</>
              )}
            </Button>
          )}

          {suggestions.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  <strong>{selected.size}</strong> de {suggestions.length} seleccionado(s)
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {provider && <span className="truncate max-w-[180px]">{provider.split("/")[0]}</span>}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSuggest}
                    disabled={loading}
                    className="h-7 text-xs"
                    data-testid="supplier-ai-regenerate"
                  >
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Regenerar"}
                  </Button>
                </div>
              </div>

              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {suggestions.map((s, idx) => {
                  const meta = getCategoryMeta(s.category_hint);
                  const Icon = meta.Icon;
                  const checked = selected.has(idx);
                  return (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                        checked ? "border-violet-300 bg-violet-50/50" : "border-border hover:border-gray-300"
                      }`}
                      data-testid={`supplier-ai-item-${idx}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(idx)}
                        className="mt-1"
                      />
                      <div className="shrink-0 h-9 w-9 rounded-lg bg-card border border-border flex items-center justify-center">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{s.name}</span>
                          <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.color}`}>
                            {meta.label}
                          </span>
                        </div>
                        {s.address && <div className="text-xs text-muted-foreground mt-0.5">{s.address}</div>}
                        {s.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.notes}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
                <Button
                  onClick={handleCreate}
                  disabled={saving || selected.size === 0}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                  data-testid="supplier-ai-create-btn"
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creando…</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" /> Añadir {selected.size} proveedor{selected.size === 1 ? "" : "es"}</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
